// src/pages/super-admin/Clinics/ClinicTestsMultiSelect.tsx
import { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import type { TestType } from "@/api/testApi";

interface ClinicTestsMultiSelectProps {
  availableTests: TestType[];
  selectedTestIds: number[];
  onChange: (selectedIds: number[]) => void;
  disabled?: boolean;
}

export default function ClinicTestsMultiSelect({
  availableTests,
  selectedTestIds,
  onChange,
  disabled = false,
}: ClinicTestsMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ✅ ADDED: Log to verify data is being received
  useEffect(() => {
    console.log("ClinicTestsMultiSelect received:", {
      availableTests: availableTests.length,
      selectedTestIds: selectedTestIds,
    });
  }, [availableTests, selectedTestIds]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleTest = (testId: number) => {
    console.log("Toggle test:", testId, "Current selection:", selectedTestIds);
    const updated = selectedTestIds.includes(testId)
      ? selectedTestIds.filter((id) => id !== testId)
      : [...selectedTestIds, testId];
    console.log("New selection:", updated);
    onChange(updated);
  };

  // ✅ FIXED: Get selected tests with proper data matching
  const selectedTests = availableTests.filter((test) =>
    selectedTestIds.includes(test.id)
  );

  // Get active tests
  const activeTests = availableTests.filter((test) => test.status === "ACTIVE");

  console.log("Render state:", {
    selectedTestIds,
    selectedTests: selectedTests.map((t) => ({ id: t.id, name: t.testName })),
    availableTests: availableTests.length,
    activeTests: activeTests.length,
  });

  return (
    <div className="relative" ref={containerRef}>
      {/* Dropdown Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full border border-gray-300 rounded px-3 py-2 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-50 hover:border-gray-400 transition-colors"
      >
        <span className="text-sm">
          {selectedTests.length === 0
            ? "Select test types..."
            : `${selectedTests.length} test(s) selected`}
        </span>
        <ChevronDown
          size={20}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
          <div className="max-h-64 overflow-y-auto">
            {activeTests.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No active test types available
              </div>
            ) : (
              activeTests.map((test) => {
                const isSelected = selectedTestIds.includes(test.id);
                console.log(
                  `Test ${test.id} (${test.testName}): selected=${isSelected}`
                );

                return (
                  <label
                    key={test.id}
                    className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected} // ✅ FIXED: Use isSelected variable
                      onChange={() => handleToggleTest(test.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 cursor-pointer"
                    />
                    <span className="ml-3 flex-1">
                      <div className="font-medium text-sm">{test.testName}</div>
                      <div className="text-xs text-gray-500">
                        {test.testCategory}
                      </div>
                    </span>
                    {isSelected && (
                      <span className="text-blue-600 font-bold">✓</span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Selected Tests Display */}
      {selectedTests.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedTests.map((test) => (
            <span
              key={test.id}
              className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
            >
              {test.testName}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
